import { validateEmail } from 'helpers/email';
import { EnableDisableValue } from 'views/Company/CompanyView/CompanyView.helpers';
import * as yup from 'yup';
import styles from './OtherSection.module.scss';

export const LEGAL_ACKNOWLEDGEMENTS_ENABLING_DISCLAIMER = (
  <>
    When this setting is enabled, users will need to acknowledge the AI
    generated content before they can:
    <ul className={styles.disclaimerList}>
      <li>Confirm a Role</li>
      <li>Access the candidate's interview guide</li>
      <li>Share interview evaluations</li>
    </ul>
  </>
);

export const LEGAL_ACKNOWLEDGEMENTS_DISABLING_DISCLAIMER = (
  <>
    When this setting is disabled, users will have direct access to:
    <ul className={styles.disclaimerList}>
      <li>Create a Role</li>
      <li>Access the candidate's interview guide</li>
      <li>Share interview evaluations</li>
    </ul>
    This setting is recommended for an easier and seamless experience.
  </>
);

export const LIVE_CHAT_ENABLING_DISCLAIMER =
  'When this setting is enabled, candidates with roles at this company see the live chat launcher in the candidate app, and flows such as reasonable adjustments mention live chat as a way to contact us.';

export const LIVE_CHAT_DISABLING_DISCLAIMER =
  'When this setting is disabled, the live chat launcher is hidden from candidates whose roles are all at companies with live chat disabled, and references to contacting us by live chat are removed.';

/** Spotted Zebra's default candidate support address (CR-473). */
export const DEFAULT_CANDIDATE_SUPPORT_EMAIL = 'candidate@spottedzebra.co.uk';

export const ALTERNATIVE_SUPPORT_EMAIL_LABEL =
  'Enable alternative support email address';
export const ALTERNATIVE_SUPPORT_EMAIL_ADDRESS_LABEL =
  'Alternative support email address';

export const ALTERNATIVE_SUPPORT_EMAIL_ENABLING_DISCLAIMER = (
  <>
    When this setting is enabled, candidates of this company are told to
    contact the address below instead of {DEFAULT_CANDIDATE_SUPPORT_EMAIL}:
    <ul className={styles.disclaimerList}>
      <li>In the candidate invitation and reminder emails</li>
      <li>In the reasonable adjustments flow</li>
      <li>In the assessment instructions ("Contact us")</li>
    </ul>
  </>
);

export const ALTERNATIVE_SUPPORT_EMAIL_DISABLING_DISCLAIMER = (
  <>
    When this setting is disabled, candidates of this company are told to
    contact {DEFAULT_CANDIDATE_SUPPORT_EMAIL}. The alternative address is kept
    and is not shown anywhere.
  </>
);

export const EXPLICIT_CONSENT_EMAIL_LABEL = 'AI explicit consent email';
export const EXPLICIT_CONSENT_EMAIL_NOT_SET = 'Not set';

/**
 * Where AI interview consent declines are emailed. Optional: empty falls
 * back to the alternative support email when enabled, else the default.
 */
export const EXPLICIT_CONSENT_EMAIL_HINT =
  'Where candidates who decline AI interview consent are reported. Leave empty to use the alternative support email address, or the default support address when that is disabled.';

export type OtherSectionFormValues = {
  isLegalAcknowledgementsEnabled: string;
  isAlternativeSupportEmailEnabled: string;
  alternativeSupportEmail: string;
  explicitConsentEmail: string;
  isLiveChatEnabled: string;
};

/**
 * The alternative address is required, and must be a valid email, only while
 * the "Enable alternative support email address" toggle is on. Mirrors the
 * monolith's `CompanyValidator.validateAlternativeSupportEmail`.
 */
export const otherSectionValidationSchema = yup.object().shape({
  isLegalAcknowledgementsEnabled: yup.string().required(),
  isAlternativeSupportEmailEnabled: yup.string().required(),
  isLiveChatEnabled: yup.string().required(),
  alternativeSupportEmail: yup
    .string()
    .test(
      'alternativeSupportEmailRequired',
      'This is a required field',
      (value, { parent }) =>
        parent.isAlternativeSupportEmailEnabled !==
          EnableDisableValue.Enabled || Boolean(value?.trim())
    )
    .test(
      'alternativeSupportEmailValidation',
      'Invalid email',
      (value, { parent }) =>
        parent.isAlternativeSupportEmailEnabled !==
          EnableDisableValue.Enabled ||
        !value?.trim() ||
        validateEmail(value.trim())
    ),
  explicitConsentEmail: yup
    .string()
    .test(
      'explicitConsentEmailValidation',
      'Invalid email',
      value => !value?.trim() || validateEmail(value.trim())
    ),
});
